/**
 *==================================================
 * Licensed Materials - Property of HCL Technologies
 *
 * HCL Commerce
 *
 * (C) Copyright HCL Technologies Limited 2020
 *
 *==================================================
 */
//Standard libraries
import { createReducer, AnyAction } from "@reduxjs/toolkit";
//Custom libraries
import { PAGINATION_CONFIGS } from "../../configs/catalog";
import { CommerceEnvironment, SEARCH_FIND_PROFILE, SEARCH_PLP_PROFILE, SELLER_FACET } from "../../constants/common";
import { getSite } from "../../_foundation/hooks/useSite";
//Redux
import * as ACTIONS from "../action-types/catalog";
import initStates from "./initStates";
import i18n from "../../i18n";
import { CatalogReducerState } from "./reducerStateInterface";
import { SORT_OPTIONS } from "../../constants/catalog";

const getStoreName = (site) => {
  let rc = "";
  if (site) {
    const loc = i18n.languages[0];
    const key = `__storeName_${loc}`;

    if (site[key]) {
      rc = site[key];
    } else if (site?.storeName) {
      const { storeCfg, defaultLanguageID: def, storeName } = site;
      const { description: d } = storeCfg ?? {};
      const id = CommerceEnvironment.reverseLanguageMap[loc.split("-").join("_")];
      const byLang = d?.find(({ languageId: l }) => l === id)?.displayName;
      const name = byLang ?? d?.find(({ languageId: l }) => l === def)?.displayName;
      rc = name ?? storeName;
      Object.assign(site, { [key]: rc });
    }
  }

  return rc;
};

const adjustFacets = ({ state, facets, selectedSellers: sellers, site }) => {
  if (facets?.length > 0) {
    const newFacetPrice = facets.find((f) => f.value.startsWith("price_"));
    state.facetPrice = newFacetPrice;

    if (sellers?.length === 1 || site?.isB2B) {
      state.facets = facets.filter((f) => f.value !== SELLER_FACET);
    } else {
      const f = facets.find((f) => f.value === SELLER_FACET);
      const empty = f?.entry?.find((e) => !e.label);
      if (empty) {
        empty.label = getStoreName(site);
      }
      state.facets = facets;
    }
  } else {
    state.facets = state.facetPrice ? [state.facetPrice] : [];
  }
};

/**
 * Catalog reducer
 * handles states used by catalog related components
 * @param state State object managed by catalog reducer
 * @param action The dispatched action
 */
const catalogReducer = createReducer(initStates.catalog, (builder) => {
  const pageDefaultOffset: number = PAGINATION_CONFIGS.pageDefaultOffset;

  builder.addCase(ACTIONS.PRODUCT_LIST_GET_REQUESTED, (state: CatalogReducerState, action: AnyAction) => {
    const payload = action.payload;
    const site = getSite();
    // console.log("PRODUCT_LIST_GET_REQUESTED: ", payload);

    // the purpose to update below states is to reflect user changes immediately to UI. A fetch call is sent in saga
    // which then updates these same states in PRODUCT_LIST_GET_SUCCESS reducer
    if (payload.states) {
      if (payload.states["selectedFacets"] !== undefined) {
        state.selectedFacets = payload.states.selectedFacets;
      }
      if (payload.states["selectedFacetLimits"] !== undefined) {
        state.selectedFacetLimits = payload.states.selectedFacetLimits;
      }
      if (payload.states["minPrice"] !== undefined) {
        state.selectedFacetPrices.min = payload.states.minPrice;
      }
      if (payload.states["maxPrice"] !== undefined) {
        state.selectedFacetPrices.max = payload.states.maxPrice;
      }
      if (payload.states["promotionEnabled"] !== undefined) {
        state.promotionEnabled = payload.states.promotionEnabled;
      }

      if (payload.states["selectedPageOffset"] >= 0) {
        state.selectedPageOffset = payload.states.selectedPageOffset;
      }
      if (payload.states["selectedSortOption"] !== undefined) {
        state.selectedSortOption = payload.states.selectedSortOption;
      }
      if (payload.states["pageNum"] !== undefined) {
        state.pageNum = payload.states.pageNum;
      }
    } else {
      state.selectedFacetLimits = [];
      state.selectedFacets = {};
      state.selectedFacetPrices.min = -1;
      state.selectedFacetPrices.max = -1;
      state.selectedPageOffset = pageDefaultOffset;
      state.selectedSortOption = SORT_OPTIONS.getDefaultSortValue(
        "searchTerm" in payload.parameters ? SEARCH_FIND_PROFILE : SEARCH_PLP_PROFILE,
        site?.storeCfg?.userData?.SENSFUEL_RANKING_ENABLED === "true"
      );
      state.pageNum = 1;
    }
  });

  builder.addCase(ACTIONS.PRODCUT_LIST_FETCHING, (state: CatalogReducerState, action: AnyAction) => {
    state.isFetching = action.payload;
  });

  builder.addCase(ACTIONS.PRODUCT_LIST_GET_SUCCESS, (state: CatalogReducerState, action: AnyAction) => {
    const response = action.response;
    const payload = action.payload;
    const { selectedSellers } = payload;
    const { facets } = response;
    const site = getSite();
    // console.log("PRODUCT_LIST_GET_SUCCESS", payload, response);
    state.catalogueURL = String(window.location.href).split("?")[0];

    if (response?.facets) {
      let min: number = 0;
      let max: number = 0;
      const priceFacet = response?.facets?.find(
        (facet) => facet.name.includes("Price") || facet.value.includes("price")
      );
      if (priceFacet) {
        min = Math.floor(response.minFilterPrice);
        max = Math.ceil(response.maxFilterPrice);
        state.possiblePricesRange = [min, max];
      }
    }

    adjustFacets({ state, facets, selectedSellers, site });

    const newProducts = response["contents"] ? response.contents : [];
    const shouldAppend = payload?.states?.appendProducts === true;
    const shouldPrepend = payload?.states?.prependProducts === true;

    state.productList = shouldPrepend
      ? [...newProducts, ...state.productList]
      : shouldAppend
      ? [...state.productList, ...newProducts]
      : newProducts;
    state.productListTotal = response.total ?? 0;
    if (response["metaData"]) {
      state.priceMode = response.metaData.price;
    }
    state.aggregatedData = {
      reviewCount: response.reviewCount ?? "",
      averageRating: response.averageRating ?? "",
      minRating: response.minRating ?? "",
      maxRating: response.maxRating ?? "",
      catentryCount: response.catentryCount ?? "",
      maxPrice: response.maxPrice ?? "",
      minPrice: response.minPrice ?? "",
    };
    state.breadcrumbs = response["breadCrumbTrailEntryView"] ? response.breadCrumbTrailEntryView : [];
    state.selectFacetRemove = payload.states ? true : false;

    //attempt to complete rest of the states from local states
    if (payload?.states) {
      if (payload.states["selectedFacets"] !== undefined) {
        state.selectedFacets = payload.states.selectedFacets;
      }
      if (payload.states["selectedFacetLimits"] !== undefined) {
        state.selectedFacetLimits = payload.states.selectedFacetLimits;
      }

      if (payload.states["minPrice"] !== undefined) {
        state.selectedFacetPrices.min = payload.states.minPrice;
      }
      if (payload.states["maxPrice"] !== undefined) {
        state.selectedFacetPrices.max = payload.states.maxPrice;
      }
      if (payload.states["promotionEnabled"] !== undefined) {
        state.promotionEnabled = payload.states.promotionEnabled;
      }

      if (payload.states["selectedPageOffset"] >= 0) {
        state.selectedPageOffset = payload.states.selectedPageOffset;
      }
      if (payload.states["selectedSortOption"] !== undefined) {
        state.selectedSortOption = payload.states.selectedSortOption;
      }
      if (payload.states["pageNum"] !== undefined) {
        state.pageNum = payload.states.pageNum;
      }
    } else {
      state.selectedFacetLimits = [];
      state.selectedFacets = {};
      state.selectedFacetPrices = {
        min: -1,
        max: -1,
      };
      state.selectedPageOffset = pageDefaultOffset;
      state.selectedSortOption = SORT_OPTIONS.getDefaultSortValue(
        "searchTerm" in payload.parameters ? SEARCH_FIND_PROFILE : SEARCH_PLP_PROFILE,
        site?.storeCfg?.userData?.SENSFUEL_RANKING_ENABLED === "true"
      );
      state.pageNum = 1;
    }
  });

  builder.addCase(ACTIONS.PRODUCT_LIST_RESET_REQUESTED, (state: CatalogReducerState, action: AnyAction) => {
    const site = getSite();
    // console.log("RESET ACTION", state, action);
    state.isFetching = null;
    state.productList = [];
    state.productListTotal = -1;
    state.selectedPageOffset = PAGINATION_CONFIGS.pageDefaultOffset;
    state.pageNum = 1;
    const { parameters } = action.payload;
    state.promotionEnabled = false;
    state.possiblePricesRange = [];
    state.facets = null;
    state.facetPrice = null;
    state.selectedFacetLimits = [];
    state.breadcrumbs = [];
    state.selectedFacets = {};
    state.selectedFacetPrices = {
      min: -1,
      max: -1,
    }; /* they are used to show selected slider and decide whether to include price query on initial load in useEffect in use-product-grid-layout.tsx */
    state.selectedSortOption = SORT_OPTIONS.getDefaultSortValue(
      parameters?.query?.profileName === SEARCH_FIND_PROFILE ? SEARCH_FIND_PROFILE : SEARCH_PLP_PROFILE,
      site?.storeCfg?.userData?.SENSFUEL_RANKING_ENABLED === "true"
    );
  });

  builder.addCase(ACTIONS.PRODUCT_LIST_FOR_PDP_GET_SUCCESS, (state: CatalogReducerState, action: AnyAction) => {
    const payload = action.payload;
    const response = action.response;

    state.aggregatedData = {
      reviewCount: response.reviewCount ?? "",
      averageRating: response.averageRating ?? "",
      minRating: response.minRating ?? "",
      maxRating: response.maxRating ?? "",
      catentryCount: response.catentryCount ?? "",
      maxPrice: response.maxPrice ?? "",
      minPrice: response.minPrice ?? "",
    };

    state.breadcrumbs = response["breadCrumbTrailEntryView"] ? response.breadCrumbTrailEntryView : [];
    if (payload.parameters.productName !== undefined && state.breadcrumbs.length > 0) {
      state.breadcrumbs = state.breadcrumbs.concat({
        label: payload.parameters.productName,
      });
    }
  });

  builder.addCase(ACTIONS.SET_BREADCRUMBS, (state: CatalogReducerState, action: AnyAction) => {
    const payload = action.payload;
    const response = action.response;

    /* HERE AGGREGATED DATA IS BEING SET FOR PDP, THIS CATALOG STATE IS COMMON BETWEEN PLP AND PDP */
    state.aggregatedData = {
      reviewCount: response.data?.contents?.[0]?.["custom.catgroup.reviewCount.raw"] ?? "",
      averageRating: response.data?.contents?.[0]?.["custom.catgroup.averageRating.raw"] ?? "",
      minRating: response.data?.contents?.[0]?.["custom.catgroup.minRating.raw"] ?? "",
      maxRating: response.data?.contents?.[0]?.["custom.catgroup.maxRating.raw"] ?? "",
      catentryCount: response.data?.contents?.[0]?.["custom.catgroup.catentryCount.raw"] ?? "",
      maxPrice: response.data?.contents?.[0]?.["custom.catgroup.maxPrice.raw"] ?? "",
      minPrice: response.data?.contents?.[0]?.["custom.catgroup.minPrice.raw"] ?? "",
    };

    state.breadcrumbs = response.data?.contents?.[0]?.UserData?.[0]?.breadCrumbTrailEntryView || [];
    if (payload.parameters.productName) {
      state.breadcrumbs = state.breadcrumbs.concat({
        label: payload.parameters.productName,
      });
    }
  });

  builder.addCase(ACTIONS.RESET_BREADCRUMBS, (state: CatalogReducerState, action: AnyAction) => {
    state.breadcrumbs = [];
  });

  // this will maintain a cache of products responses (full products response)
  builder.addCase(ACTIONS.PRODUCT_LIST_DEETS_S, (state: CatalogReducerState, action: AnyAction) => {
    const { response } = action;
    const { contents } = response;
    const cache: any = state.productCache;
    const max = cache.MAX;
    const n = contents.length >= max ? max : contents.length;
    let start = contents.length >= max ? 0 : cache.idx;

    // for products in response
    for (let i = 0; i < n; ++i) {
      const pNew = contents[i];
      const pOld = cache.container[start];

      // update or add to array
      if (pOld) {
        delete cache.byId[pOld.id];
        cache.container[start] = pNew;
      } else {
        cache.container.push(pNew);
      }

      // add to map for easy indexing
      cache.byId[pNew.id] = pNew;

      // remember to modulo max so that we do a return trip to the start of the array
      start = (start + 1) % max;
    }

    // update the index for next cache attempt
    cache.idx = start;
  });

  builder.addCase(ACTIONS.SET_IS_INVALID_URL, (state: CatalogReducerState, action: AnyAction) => {
    state.isInvalidUrl = action.payload;
  });
});

export default catalogReducer;
